import moment from "moment";
import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { removeDashed, showToast } from "../../../common/utils/Functions";
import DashboardLayout from "../../../components/layouts/DashboardLayout";
import PageHeading from "../../../components/pageHeading";
import ReactLoader from "../../../components/reactLoader/ReactLoader";
import ReactTable from "../../../components/reactTable/ReactTable";
import SetUpSuccess from "../../../components/stripeSetupIndication/SetUpSuccess";
import useFetch from "../../../hooks/useFetch";
import styles from "../paymentManagement/payment.module.scss";
import GetPaidModal from "./GetPaidModal";
import Reject from "./reject";
import Success from "./success";
import ThemeButton from "../../../components/theme/ThemeButton";
import TipAmountModal from "../../../components/tipAmountModal/tipAmountModal";

const PaymentManagement = () => {
  const [stripeStatus, setStripeStatus] = useState<number | null>(null);
  const [showGetPaidModal, setShowGetPaidModal] = useState(false);
  const [withDrawHistoryData, setWithDrawHistoryData] = useState<any[]>([]);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showTip, setShowTip] = useState(false);
  const [dashboardTipItems, setDashboardTipItems] = useState<any>([]);

  // Show property-aware subtitle only for organizations that have properties
  const { propertiesCount } = useSelector((store: RootState) => store.userData);
  const resolvedPropertiesCount =
    typeof propertiesCount === "number"
      ? propertiesCount
      : Number(localStorage.getItem("properties_count") ?? "0");
  const hasProperties = resolvedPropertiesCount > 0;
  const payoutSubHeading = hasProperties
    ? "Here you can manage your default payout settings for your organization. You can set different payout accounts for individual properties under their Property Information page."
    : "Here you can manage your payout settings.";

  const handleTipClose = () => setShowTip(false);




  const [
    paymentStatusApi,
    { response: statusResponse, loading: statusLoading, error: statusError },
  ] = useFetch("organization/check-charge-enable");

  const [
    transferBalanceApi,
    { response: transferBalanceResponse, loading: transferBalanceLoading, error: transferBalanceError },
  ] = useFetch("/common/user/transfer-to-connect-account", {
    method: "post"
  });

  const [
    withdrawHistoryApi,
    { response: withdrawHistoryResponse, loading: withdrawHistoryLoading, error: withdrawHistoryError },
  ] = useFetch("/common/user/payout-widrawl-list", {
    method: "post"
  });

  const [
    paymentApi,
    { response: paymentResponse, loading: paymentLoading, error: paymentError },
  ] = useFetch("organization/stripe-integration");


  useEffect(() => {
    if (paymentResponse) {
      const url = paymentResponse?.data?.url;
      window.open(url, "_blank");
      // Close modal after redirect
      setShowGetPaidModal(false);
    }
    if (paymentError) {

      showToast(paymentError.message as string, "error");

      // Close modal on error as well
      setShowGetPaidModal(false);
    }
  }, [paymentResponse, paymentError]);

  /** useEffect for  list */
  useEffect(() => {
    const timer = setTimeout(() => {
      paymentStatusApi();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (statusResponse) {
      setStripeStatus(
        statusResponse?.data?.organization_details?.stripe_status
      );
    }
    if (statusError) {
      showToast(statusError.message as string, "error");
    }
  }, [statusResponse, statusError]);


  useEffect(() => {
    if (transferBalanceResponse) {
      // Show modal with transfer balance details
      setDashboardTipItems([
        {
          label: "Available Balance",
          value: transferBalanceResponse?.data?.[0]?.gross_amount
            ? `$${transferBalanceResponse?.data?.[0]?.gross_amount?.toFixed(2)}`
            : removeDashed,
        },
        {
          label: "Transaction Fees",
          value: '$0.00'
        },
        {
          label: "Transferred Amount",
          value: transferBalanceResponse?.data?.[0]?.transferred_amount
            ? `$${transferBalanceResponse?.data?.[0]?.transferred_amount?.toFixed(2)}`
            : removeDashed,
        },
        {
          label: "Remaining Balance",
          value: '$0.00',
        },
      ]);
      setShowTip(true);
    }
    if (transferBalanceError) {

      showToast(transferBalanceError.message as string, "error");

    }
  }, [transferBalanceResponse, transferBalanceError]);

  // Trigger withdraw history API when stripe status is 2 or 1, or when manual transfer is enabled for status 0,3,4,5
  useEffect(() => {
    if (stripeStatus === 2 || stripeStatus === 1 ||
      (statusResponse?.data?.is_manual_transfer === 1 && (stripeStatus === 0 || stripeStatus === 3 || stripeStatus === 4 || stripeStatus === 5))) {
      withdrawHistoryApi();
    }
  }, [stripeStatus, statusResponse?.data?.is_manual_transfer, transferBalanceResponse]);



  const handleGetPaidClick = () => {
    if (stripeStatus === 2 || stripeStatus === 1) {
      // For stripe status 2, call the transfer API
      const stripeConnectAccountId = statusResponse?.data?.organization_details?.stripe_connect_account_id;
      if (stripeConnectAccountId) {
        transferBalanceApi({
          accountIds: [stripeConnectAccountId]
        });
      } else {
        showToast("Stripe account ID not found", "error");
      }
    } else {
      // For other stripe statuses, show the modal
      setShowGetPaidModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowGetPaidModal(false);
  };

  const handleStripeSetup = () => {
    // Don't close modal immediately - let the API response handle it
    paymentApi();
  };

  // Helper function to render withdrawal history table
  const renderWithdrawalHistoryTable = () => {
    if (withDrawHistoryData.length > 0) {
      return (
        <div style={{ marginTop: '20px' }}>
          <ReactTable
            data={withDrawHistoryData}
            headings={headings}
            actionColumn={false}
            statusColumn={false}
            currentPage={currentPage}
            itemPerPage={pageLimit}
            totalItems={totalRecords}
            setPerPageLimit={setPageLimit}
            pageLimit={pageLimit}
            setCurrentPage={setCurrentPage}
            loading={withdrawHistoryLoading}
            pagination={false}
          >
            <div
              className={styles.dashboardHeadings}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 10px",
              }}
            >
              <h2>Balance Withdrawal History</h2>
            </div>
          </ReactTable>
        </div>
      );
    }
    return null;
  };

  const headings = [
    {
      key: "balance",
      label: "Balance",
    },
    {
      key: "transaction_fee",
      label: "Transaction Fee",
    },
    {
      key: "amount_received",
      label: "Amount Received ",
    },
    {
      key: 'date',
      label: 'Date'
    }
  ];

  useEffect(() => {
    if (withdrawHistoryResponse) {
      let updatedList;
      updatedList = withdrawHistoryResponse.data?.map((item: any) => ({
        balance: item?.total_amount ? item?.total_amount?.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }) : removeDashed,
        transaction_fee: "$0.00",
        amount_received: item?.net_tip_amount ? item?.net_tip_amount?.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }) : removeDashed,
        date: item?.createdAt ? moment(item?.createdAt).format("MM/DD/YYYY") : removeDashed,
      }))

      setWithDrawHistoryData(updatedList);
    } if (withdrawHistoryError) {
      showToast(withdrawHistoryError.message as string, "error");
    }
  }, [withdrawHistoryResponse, withdrawHistoryError])

  useEffect(() => {
    if (transferBalanceResponse) {
      paymentStatusApi();
    }
  }, [transferBalanceResponse])

  return (
    <DashboardLayout>
      {statusLoading && !stripeStatus ? (
        <ReactLoader />
      ) : (
        <Container>
          <PageHeading
            heading="Payout Settings"
            subHeading={payoutSubHeading}
          />

          {
            statusResponse?.data?.total_escrowed_tips > 0 || withDrawHistoryData.length > 0 || (statusResponse?.data?.organization_details?.stripe_status !== 2 && statusResponse?.data?.organization_details?.stripe_status !== 1) ? <div className="d-flex gap-5" style={{ margin: '40px 0px 20px' }}>

              <div>
                <h6 className="mb-0">Available Balance</h6>
                <p style={{ color: '#2549E8', fontWeight: 600 }}>
                  {statusResponse?.data?.total_escrowed_tips
                    ? `$${statusResponse?.data?.total_escrowed_tips?.toFixed(2)}`
                    : '$0.00'
                  }
                </p>
              </div>

              {statusResponse?.data?.total_escrowed_tips > 0 && (<ThemeButton
                style={{ height: '40px' }}
                onClick={handleGetPaidClick}
                loading={transferBalanceLoading}
                disabled={transferBalanceLoading}
              >
                Get Paid
              </ThemeButton>)}
              {
                (statusResponse?.data?.is_transfer_enabled === 1 || statusResponse?.data?.organization_details?.transfer_status === 0) && (
                  <div className={styles.note}>
                    <p>
                      Your transfer cannot be completed at this time. Please try again later.
                      If this problem continues, please contact support at  <a href="mailto:support@toptipper.com">support@toptipper.com</a>.
                    </p>
                  </div>
                )
              }
            </div> : null
          }

          {stripeStatus === 0 ? (
            <>
              <SetUpSuccess status={stripeStatus} />
              {statusResponse?.data?.is_manual_transfer === 1 && renderWithdrawalHistoryTable()}
            </>
          ) : stripeStatus === 1 ? (
            <>
              <Success />
              {renderWithdrawalHistoryTable()}
            </>
          ) : stripeStatus === 2 ? (
            <>
              <Success />
              {renderWithdrawalHistoryTable()}
            </>
          ) : stripeStatus === 3 ? (
            <>
              <Reject status={stripeStatus} />
              {statusResponse?.data?.is_manual_transfer === 1 && renderWithdrawalHistoryTable()}
            </>
          ) : stripeStatus === 4 ? (
            <>
              <Reject status={stripeStatus} />
              {statusResponse?.data?.is_manual_transfer === 1 && renderWithdrawalHistoryTable()}
            </>
          ) : stripeStatus === 5 ? (
            <>
              <Reject status={stripeStatus} />
              {statusResponse?.data?.is_manual_transfer === 1 && renderWithdrawalHistoryTable()}
            </>
          ) : null}
        </Container>
      )}

      <GetPaidModal
        show={showGetPaidModal}
        handleClose={handleCloseModal}
        onStripeClick={handleStripeSetup}
        loading={paymentLoading}
      />



      <TipAmountModal
        show={showTip}
        handleClose={handleTipClose}
        title="Payout Complete"
        tipItems={dashboardTipItems}
        isNoteShow={true}
      >

        <ThemeButton onClick={() => handleTipClose()}>
          Close
        </ThemeButton>
      </TipAmountModal>

    </DashboardLayout>
  );
};

export default PaymentManagement;
